import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { ArchivedFirms } from "@/components/archived-firms";
import { DeleteForever } from "@/components/delete";
import { Button, ErrorNote, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { useRefreshDirectory } from "@/lib/queries";

/*
 * LE PAGINE «ARCHIVIATI»: una per Clienti, Prop firm e Broker, raggiunte dal
 * pulsante «Archiviati» accanto a «Nuovo …». Prima stavano in fondo all'elenco,
 * dopo le righe attive, dove si scambiavano per parte dell'elenco.
 *
 * Archiviare non cancella: da qui la riga torna attiva con tutti i suoi dati
 * (Ripristina) o si elimina per davvero (Elimina, con la sua conferma).
 */

const FRASE = "Archiviato: nascosto ovunque, non cancellato. Ripristina lo rimette attivo con tutti i suoi dati.";
const FRASE_BROKER = "Archiviato: nascosto ovunque, non cancellato. Ripristina lo rimette attivo con i suoi server.";
/** Per le firm: il ripristino riporta UNA riga, e i programmi archiviati tornano uno per uno. */
const FRASE_FIRM = "Archiviata: nascosta ovunque, non cancellata. Ripristina la rimette attiva; i suoi programmi archiviati si ripristinano uno per uno.";

const Indietro = ({ to, label }: { to: string; label: string }) => (
  <Link to={to}>
    <Button>{label}</Button>
  </Link>
);

export function ArchivedClients() {
  const canWrite = useCanWrite();
  const refresh = useRefreshDirectory();
  const q = useQuery({ queryKey: ["archived"], queryFn: () => api.archived() });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ripristina = async (che: "cliente" | "conto", id: string) => {
    setBusy(id);
    setError(null);
    try {
      await (che === "cliente" ? api.clients.restore(id) : api.accounts.restore(id));
      await q.refetch();
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const clients = q.data?.clients ?? [];
  const accounts = q.data?.accounts ?? [];

  return (
    <>
      <PageHeader title="Clienti archiviati" subtitle={FRASE} actions={<Indietro to="/clienti" label="Clienti" />} />
      <div className="space-y-3 px-6 py-4">
        {q.isLoading ? (
          <p className="text-sm text-muted">Caricamento…</p>
        ) : q.error ? (
          <ErrorNote>{errorMessage(q.error)}</ErrorNote>
        ) : !clients.length && !accounts.length ? (
          <p className="text-sm text-muted">Nessun archiviato: ciò che archivi da Clienti compare qui, e da qui torna.</p>
        ) : (
          <div className="space-y-1.5">
            {clients.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-faint">Cliente</span>
                <span className="text-ink">{c.name}</span>
                <span className="text-muted">{c.email}</span>
                {canWrite && (
                  <>
                    <Button size="sm" variant="ghost" disabled={busy === c.id} onClick={() => void ripristina("cliente", c.id)}>
                      {busy === c.id ? "…" : "Ripristina"}
                    </Button>
                    <DeleteForever kind="cliente" id={c.id} label={c.name} size="sm" onDeleted={() => void q.refetch()} />
                  </>
                )}
              </div>
            ))}
            {accounts.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-faint">Conto</span>
                <span className="text-ink">{a.label}</span>
                <span className="font-mono text-xs text-muted">{a.mt5Login}</span>
                <span className="text-muted">{a.server.name}</span>
                {/*
                  * Il conto di un cliente archiviato non si può ripristinare, e
                  * dirlo QUI è meglio che lasciarglielo scoprire da un rifiuto: il
                  * pulsante c'è comunque, perché il motivo lo dà il backend con
                  * l'ordine da seguire.
                  */}
                {a.client.isArchived && <span className="text-xs text-warn">prima il cliente {a.client.name}</span>}
                {canWrite && (
                  <>
                    <Button size="sm" variant="ghost" disabled={busy === a.id} onClick={() => void ripristina("conto", a.id)}>
                      {busy === a.id ? "…" : "Ripristina"}
                    </Button>
                    <DeleteForever kind="conto" id={a.id} label={a.label} size="sm" onDeleted={() => void q.refetch()} />
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        {error && <p className="text-xs text-loss">{error}</p>}
      </div>
    </>
  );
}

export function ArchivedFirmsPage({ kind }: { kind: "PROP" | "BROKER" }) {
  const isProp = kind === "PROP";
  return (
    <>
      <PageHeader
        title={isProp ? "Prop firm archiviate" : "Broker archiviati"}
        subtitle={isProp ? FRASE_FIRM : FRASE_BROKER}
        actions={<Indietro to={isProp ? "/prop-firm" : "/broker"} label={isProp ? "Prop firm" : "Broker"} />}
      />
      <ArchivedFirms kind={kind} />
    </>
  );
}
